// FILE: src/components/admin/supportSla.jsx
// Support requests on the staff side (WP-6, MTO 10.1, 10.2, 11.2.1): how a
// request came in (online, phone, email), its 48-hour answer target as
// icon + words ("Due in 5 h", "Overdue by 3 h"), and its status history.
// The server sends `slaDueAt`, `dueInHours` and `breached`; the due time is
// re-read against the page clock so the words stay right while the page is
// open. Older payloads without those fields fall back to createdAt + 48 h.
import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import { StatusChip } from "../ui";
import { humanize, ticketStatusLabel } from "../../i18n/labels";
import { SLA_HOURS, ageHours, formatDateTime } from "./format";

const HOUR_MS = 60 * 60 * 1000;

export const CHANNELS = ["IN_APP", "PHONE", "EMAIL"];

const CHANNEL = {
  IN_APP: { label: "Online request", Icon: ComputerOutlinedIcon },
  PHONE: { label: "Phone", Icon: PhoneOutlinedIcon },
  EMAIL: { label: "Email", Icon: MailOutlineIcon }
};

export function channelLabel(channel) {
  return CHANNEL[channel || "IN_APP"]?.label || humanize(channel);
}

export function channelIcon(channel) {
  return CHANNEL[channel || "IN_APP"]?.Icon || ComputerOutlinedIcon;
}

/** How the request was opened, in words. */
export function sourceLabel(source, channel) {
  if (source === "ADMIN") return channel === "EMAIL" ? "Logged by staff from an email" : "Logged by staff from a phone call";
  if (source === "AUTO_LOCKOUT") return "Opened automatically when the account was locked";
  return "Sent by the student online";
}

function toMs(value) {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * The 48-hour target for one request:
 *   resolved  true once resolved
 *   dueIn     hours left (negative = overdue); null once resolved
 *   breached  answered late, or still open past the due time
 *   overdue   still open past the due time
 */
export function slaInfo(ticket, now = Date.now()) {
  if (!ticket) return { resolved: false, dueIn: null, breached: false, overdue: false };
  const resolved = ticket.status === "RESOLVED";
  let dueIn = null;
  if (!resolved) {
    const due = toMs(ticket.slaDueAt);
    if (due !== null) dueIn = (due - now) / HOUR_MS;
    else if (typeof ticket.dueInHours === "number") dueIn = ticket.dueInHours;
    else {
      const age = ageHours(ticket.createdAt, now);
      dueIn = age === null ? null : SLA_HOURS - age;
    }
  }
  const overdue = !resolved && ((dueIn !== null && dueIn < 0) || ticket.breached === true);
  const breached = ticket.breached === true || overdue;
  return { resolved, dueIn, breached, overdue };
}

/** 0.4 → "less than 1 h", 5.6 → "5 h", 52 → "2 days". */
export function hoursText(hours) {
  const abs = Math.abs(Number(hours) || 0);
  if (abs < 1) return "less than 1 h";
  if (abs < 48) return `${Math.floor(abs)} h`;
  const days = Math.floor(abs / 24);
  return `${days} days`;
}

/** "Due in 5 h" / "Overdue by 3 h" / "Resolved after 48 h" as words; null when there's nothing to say. */
export function slaText(ticket, now = Date.now()) {
  const s = slaInfo(ticket, now);
  if (s.resolved) return s.breached ? `Resolved after ${SLA_HOURS} h` : null;
  if (s.dueIn === null) return null;
  return s.overdue ? `Overdue by ${hoursText(s.dueIn)}` : `Due in ${hoursText(s.dueIn)}`;
}

/** The answer target as a chip (icon + words, never colour alone). */
export function SlaChip({ ticket, now, showWithinTarget = false }) {
  const s = slaInfo(ticket, now);
  if (s.resolved) {
    if (s.breached) {
      return <StatusChip tone="warning" icon={<WarningAmberOutlinedIcon />} label={`Resolved after ${SLA_HOURS} h`} />;
    }
    return showWithinTarget ? (
      <StatusChip tone="success" icon={<TaskAltOutlinedIcon />} label={`Resolved within ${SLA_HOURS} h`} />
    ) : null;
  }
  if (s.dueIn === null) return null;
  return s.overdue ? (
    <StatusChip tone="warning" icon={<WarningAmberOutlinedIcon />} label={`Overdue by ${hoursText(s.dueIn)}`} />
  ) : (
    <StatusChip tone="info" icon={<ScheduleOutlinedIcon />} label={`Due in ${hoursText(s.dueIn)}`} />
  );
}

/** "Yes" / "No" / "Not yet" for "Answered within 48 hours?" (icon + words). */
export function WithinTargetChip({ ticket, now }) {
  const s = slaInfo(ticket, now);
  if (s.resolved && !s.breached) {
    return <StatusChip tone="success" icon={<TaskAltOutlinedIcon />} label="Yes, resolved in time" />;
  }
  if (s.resolved) return <StatusChip tone="warning" icon={<WarningAmberOutlinedIcon />} label="No, resolved late" />;
  if (s.overdue) {
    return <StatusChip tone="warning" icon={<WarningAmberOutlinedIcon />} label={`No, overdue by ${hoursText(s.dueIn)}`} />;
  }
  return (
    <StatusChip
      tone="info"
      icon={<ScheduleOutlinedIcon />}
      label={s.dueIn === null ? "Not yet: still open" : `Not yet: due in ${hoursText(s.dueIn)}`}
    />
  );
}

/** How the request came in, as words with an icon. */
export function ChannelText({ channel }) {
  const Icon = channelIcon(channel);
  return (
    <Stack component="span" direction="row" spacing={0.75} sx={{ alignItems: "center", color: "text.secondary" }}>
      <Icon aria-hidden sx={{ fontSize: "1.25rem" }} />
      <Typography component="span" sx={{ color: "text.secondary" }}>
        {channelLabel(channel)}
      </Typography>
    </Stack>
  );
}

const BY_ROLE = { ADMIN: "by staff", STUDENT: "by the student", SYSTEM: "automatically" };

function historyText(entry, ticket, myId) {
  if (entry.reason === "CREATED") {
    if (ticket.source === "ADMIN")
      return entry.by && myId && String(entry.by) === String(myId) ? "Logged by you" : "Logged by staff";
    if (ticket.source === "AUTO_LOCKOUT") return "Opened automatically when the account was locked";
    return "Opened by the student";
  }
  if (entry.reason === "REOPENED_BY_MESSAGE") return "Reopened when the student added a message";
  const by = entry.by && myId && String(entry.by) === String(myId) ? "by you" : BY_ROLE[entry.byRole] || "";
  if (entry.status === "RESOLVED") return `Marked as resolved ${by}`.trim();
  if (entry.status === "OPEN") return `Reopened ${by}`.trim();
  return `${ticketStatusLabel(entry.status)} ${by}`.trim();
}

/** The history to show: the server's, or (older requests) opened/resolved from the dates. */
export function historyEntries(ticket) {
  const list = Array.isArray(ticket?.statusHistory) ? ticket.statusHistory.filter((h) => h && h.at) : [];
  if (list.length) return list;
  const out = [{ status: "OPEN", at: ticket?.createdAt, reason: "CREATED", byRole: "STUDENT" }];
  if (ticket?.resolvedAt) out.push({ status: "RESOLVED", at: ticket.resolvedAt, byRole: "ADMIN" });
  return out.filter((h) => h.at);
}

/** Status changes, oldest first, as an ordered list. */
export function StatusTimeline({ ticket, myId }) {
  const entries = historyEntries(ticket);
  if (!entries.length) return <Typography sx={{ color: "text.secondary" }}>No changes recorded yet.</Typography>;
  return (
    <Box component="ol" aria-label="Status changes, oldest first" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {entries.map((h, i) => {
        const Icon =
          h.reason === "CREATED" ? AddCircleOutlineIcon : h.status === "RESOLVED" ? TaskAltOutlinedIcon : ReplayOutlinedIcon;
        const last = i === entries.length - 1;
        return (
          <Box component="li" key={`${h.at}-${i}`} sx={{ display: "flex", gap: 1.5, pb: last ? 0 : 2, position: "relative" }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Icon aria-hidden sx={{ color: h.status === "RESOLVED" ? "primary.main" : "text.secondary" }} />
              {!last && <Box aria-hidden sx={{ flex: 1, width: "2px", bgcolor: "divider", mt: 0.5 }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600 }}>{historyText(h, ticket || {}, myId)}</Typography>
              <Typography sx={{ color: "text.secondary" }}>
                <time dateTime={new Date(h.at).toISOString()}>{formatDateTime(h.at)}</time>
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
